import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { portfolioStore } from '../../stores/portfolio-store';
import { Button, Input } from '@privemind/components';
import { Asset } from '../../types';
import './edit-asset-modal.scss';

interface EditAssetModalProps {
  asset: Asset;
  assetId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const EditAssetModal: React.FC<EditAssetModalProps> = observer(({
  asset,
  assetId,
  isOpen,
  onClose,
}) => {
  const [formData, setFormData] = useState({
    amount: asset.amount.toString(),
    avgBuyPrice: asset.avgBuyPrice?.toString() || '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        amount: asset.amount.toString(),
        avgBuyPrice: asset.avgBuyPrice?.toString() || '',
      });
      setErrors({});
    }
  }, [isOpen, asset]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.amount.trim()) {
      newErrors.amount = 'Amount is required';
    } else {
      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        newErrors.amount = 'Amount must be a positive number';
      }
    }

    if (formData.avgBuyPrice.trim() && isNaN(parseFloat(formData.avgBuyPrice))) {
      newErrors.avgBuyPrice = 'Average buy price must be a valid number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const updatedAsset: Asset = {
        id: asset.id,
        name: asset.name,
        symbol: asset.symbol,
        amount: parseFloat(formData.amount),
        avgBuyPrice: formData.avgBuyPrice.trim() ? parseFloat(formData.avgBuyPrice) : undefined,
        bucket: asset.bucket,
        type: asset.type,
        platforms: asset.platforms,
        lastUpdated: new Date().toISOString(),
      };

      const result = await portfolioStore.updateAsset(assetId, updatedAsset);

      // Check if result is an Error or boolean
      if (result instanceof Error) {
        setErrors({ submit: `Failed to update asset: ${result.message}` });
      } else if (result) {
        onClose();
      } else {
        setErrors({ submit: 'Failed to update asset. Please try again.' });
      }
    } catch (error) {
      setErrors({ submit: 'An error occurred while updating the asset.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };


  if (!isOpen) return null;

  return (
    <div className="edit-asset-modal">
      <div className="edit-asset-modal__backdrop" onClick={onClose} />
      <div className="edit-asset-modal__content">
        <div className="edit-asset-modal__header">
          <h2>Edit Asset</h2>
          <button className="edit-asset-modal__close" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="edit-asset-modal__form">
          <div className="edit-asset-modal__form-group">
            <label>Asset Name</label>
            <Input
              value={asset.name}
              disabled
            />
          </div>

          <div className="edit-asset-modal__form-group">
            <label>Symbol</label>
            <Input
              value={asset.symbol.toUpperCase()}
              disabled
            />
          </div>

          <div className="edit-asset-modal__form-group">
            <label>Bucket</label>
            <Input
              value={asset.bucket}
              disabled
            />
          </div>

          <div className="edit-asset-modal__form-group">
            <label>Amount</label>
            <Input
              value={formData.amount}
              onChange={(value) => handleInputChange('amount', value)}
              placeholder="e.g., 0.5, 1000"
              error={!!errors.amount}
            />
            {errors.amount && <div className="edit-asset-modal__field-error">{errors.amount}</div>}
          </div>

          <div className="edit-asset-modal__form-group">
            <label>Average Buy Price (Optional)</label>
            <Input
              value={formData.avgBuyPrice}
              onChange={(value) => handleInputChange('avgBuyPrice', value)}
              placeholder="e.g., 45000, 3000"
              error={!!errors.avgBuyPrice}
            />
            {errors.avgBuyPrice && <div className="edit-asset-modal__field-error">{errors.avgBuyPrice}</div>}
          </div>

          {errors.submit && (
            <div className="edit-asset-modal__error">
              {errors.submit}
            </div>
          )}

          <div className="edit-asset-modal__actions">
            <Button
              color="secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={() => handleSubmit({} as React.FormEvent)}
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Update Asset'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
});
